<script setup>
/**
 * 对账(S8)—— 哪些调用烧了上游 token,却没有对应的计费行。
 *
 * **判据不写在页面上,写在每一行上**:归类列的徽标说这一行为什么可疑(「成功未扣款」/「失败但有用量」),
 * 一句话解释挂在 `title` 上。什么进集合、什么故意不进,是给改代码的人看的,在 `assembly/llm-admin/recon.js`
 * 和 `plan/llm-admin/` 里,不占运营的页面。
 *
 * 「成本事实未记录」的候选行后端不返回明细、只返回条数(`unpriceable`),所以它在页面上是表头旁边的一个数,
 * 不是一段话。
 */
import { computed, onMounted, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { Search } from '@lucide/vue'
import {
  Badge,
  Button,
  EmptyState,
  Input,
  PageHeader,
  Pagination,
  Section,
  SegmentedControl,
  Spinner,
  Stat,
  Table,
  Toolbar,
} from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { listUnbilledCalls } from '../../api/llm/index.js'
import { unpriceableReasons } from './recon-anomaly.js'
import { formatInt, formatMoney } from './llm-format.js'
import { DEFAULT_RECON_RANGE, RECON_RANGES, reconQuery, reconWindowOf } from './recon-window.js'

const { t } = useI18n()
const router = useRouter()

/** 后端的 `limit` 上限是 200。 */
const LIMIT_OPTIONS = [50, 100, 200].map((value) => ({ value, label: String(value) }))

const timeWindow = reactive({ ...reconWindowOf(new Date(), DEFAULT_RECON_RANGE), limit: 50 })

/** 选中的快捷区间。手改了两个输入框之后没有任何一段被选中 —— 高亮着 24 小时就是在说谎。 */
const range = ref(DEFAULT_RECON_RANGE)

const rangeOptions = computed(() =>
  RECON_RANGES.map(({ value }) => ({ value, label: t(`visualGameAdmin.llm.recon.window.range.${value}`) })),
)

/** @type {import('vue').Ref<import('../../api/llm/recon.js').Anomaly[]>} */
const anomalies = ref([])
/** @type {import('vue').Ref<import('../../api/llm/recon.js').Unpriceable|null>} */
const unpriceable = ref(null)
const loading = ref(false)
const error = ref('')
const nextCursor = ref(null)
const currentCursor = ref(null)
const cursorStack = ref([])

const columns = computed(() => [
  { key: 'created_at', label: t('visualGameAdmin.llm.recon.table.createdAt'), width: '170px' },
  { key: 'request_id', label: t('visualGameAdmin.llm.recon.table.requestId'), width: '190px', mono: true },
  { key: 'user_id', label: t('visualGameAdmin.llm.recon.table.user'), width: '130px', mono: true },
  { key: 'model', label: t('visualGameAdmin.llm.recon.table.model'), width: '210px' },
  {
    key: 'supplier_cost_micros',
    label: t('visualGameAdmin.llm.recon.table.expectedAmount'),
    width: '150px',
    align: 'right',
    mono: true,
  },
  {
    key: 'expected_charged_points',
    label: t('visualGameAdmin.llm.recon.table.expectedPoints'),
    width: '140px',
    align: 'right',
    mono: true,
  },
  { key: 'actions', label: t('visualGameAdmin.common.actions'), width: '110px', align: 'right' },
])

/** 本页候选行里「成本事实未记录」的原因,条数为 0 的不显示(它没发生)。 */
const reasons = computed(() => (unpriceable.value ? unpriceableReasons(unpriceable.value) : []))

onMounted(() => load(null))

/** @param {string|null} cursor */
async function load(cursor) {
  loading.value = true
  error.value = ''
  try {
    // 区间非法时 `reconQuery` 直接抛 —— 不兜一个默认区间偷偷替他查另一段时间。
    const page = await listUnbilledCalls(reconQuery({ ...timeWindow, limit: Number(timeWindow.limit), cursor }))
    anomalies.value = page.items
    unpriceable.value = page.unpriceable
    nextCursor.value = page.next_cursor
    currentCursor.value = cursor
  } catch (err) {
    error.value = err.message
  } finally {
    loading.value = false
  }
}

function search() {
  cursorStack.value = []
  load(null)
}

/** 点快捷区间 = 改区间 + 立刻查:少一步「再点查询」。 */
function pickRange(next) {
  range.value = next
  Object.assign(timeWindow, reconWindowOf(new Date(), next))
  search()
}

/** 手改开始 / 结束时,快捷区间的高亮跟着灭 —— 输入框里的时间才是要查的时间。 */
function editWindow(field, value) {
  timeWindow[field] = value
  range.value = ''
}

/** 每页行数改了就直接重查,不用再点一次查询。 */
function pickLimit(next) {
  timeWindow.limit = next
  search()
}

function nextPage() {
  cursorStack.value = [...cursorStack.value, currentCursor.value]
  load(nextCursor.value)
}

function previousPage() {
  const stack = [...cursorStack.value]
  const previous = stack.pop()
  cursorStack.value = stack
  load(previous)
}

/** 每一行都能落到那一次调用的日志详情上 —— 对账查出来的是「哪一次调用」,人接着要看的就是它。 */
function openCallLog(anomaly) {
  router.push({ path: '/llm/calls', query: { request_id: anomaly.request_id } })
}

/** @param {string} at */
function formatTime(at) {
  return new Date(at).toLocaleString()
}
</script>

<template>
  <div class="recon vga-page">
    <PageHeader :title="t('visualGameAdmin.llm.recon.title')" />

    <Section data-insp-path="apps/visual-game-admin/src/pages/llm/Reconciliation.vue#window">
      <Toolbar>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.llm.recon.window.rangeLabel') }}</label>
          <SegmentedControl
            :model-value="range"
            :options="rangeOptions"
            :disabled="loading"
            size="sm"
            @update:model-value="pickRange"
          />
        </div>
        <div class="drama-toolbar-field drama-toolbar-field--wide">
          <label for="recon-since">{{ t('visualGameAdmin.llm.recon.window.since') }}</label>
          <Input
            id="recon-since"
            :model-value="timeWindow.since"
            type="datetime-local"
            size="sm"
            @update:model-value="editWindow('since', $event)"
          />
        </div>
        <div class="drama-toolbar-field drama-toolbar-field--wide">
          <label for="recon-until">{{ t('visualGameAdmin.llm.recon.window.until') }}</label>
          <Input
            id="recon-until"
            :model-value="timeWindow.until"
            type="datetime-local"
            size="sm"
            @update:model-value="editWindow('until', $event)"
          />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.llm.recon.window.limit') }}</label>
          <SegmentedControl
            :model-value="timeWindow.limit"
            :options="LIMIT_OPTIONS"
            :disabled="loading"
            size="sm"
            @update:model-value="pickLimit"
          />
        </div>
        <template #actions>
          <Button
            variant="primary"
            size="sm"
            :loading="loading"
            @click="search"
          >
            <Search :size="16" />
            {{ t('visualGameAdmin.llm.recon.window.apply') }}
          </Button>
        </template>
      </Toolbar>
    </Section>

    <Section
      :title="t('visualGameAdmin.llm.recon.table.title')"
      :padded="false"
      data-insp-path="apps/visual-game-admin/src/pages/llm/Reconciliation.vue#table"
    >
      <!-- 「成本事实未记录」在这一页只是一个数:它没有明细行,后端只给条数。 -->
      <template #actions>
        <Stat
          v-for="reason in reasons"
          :key="reason.reason"
          :label="t(`visualGameAdmin.llm.recon.unpriceable.${reason.reason}.label`)"
          :value="formatInt(reason.count)"
          :title="t(`visualGameAdmin.llm.recon.unpriceable.${reason.reason}.hint`)"
          tone="warning"
        />
      </template>

      <div v-if="error" class="recon__state">
        <p class="vga-error">{{ error }}</p>
        <Button variant="secondary" @click="load(currentCursor)">
          {{ t('visualGameAdmin.common.retry') }}
        </Button>
      </div>
      <div v-else-if="loading" class="recon__state">
        <Spinner :label="t('visualGameAdmin.common.loading')" />
      </div>
      <div v-else-if="anomalies.length === 0" class="recon__state">
        <EmptyState
          :title="t('visualGameAdmin.llm.recon.empty.title')"
          :description="t('visualGameAdmin.llm.recon.empty.description')"
        />
      </div>
      <Table v-else :columns="columns" :rows="anomalies" row-key="request_id">
        <template #created_at="{ row }">
          {{ formatTime(row.created_at) }}
        </template>
        <template #user_id="{ row }">
          {{ row.user_id ?? '—' }}
        </template>
        <template #model="{ row }">
          <span :title="`${row.provider} / ${row.model}`">{{ row.provider }} / {{ row.model }}</span>
        </template>
        <template #supplier_cost_micros="{ row }">
          {{ formatMoney(row.supplier_cost_micros) }}
        </template>
        <template #expected_charged_points="{ row }">
          {{ row.expected_charged_points === null ? '—' : formatInt(row.expected_charged_points) }}
        </template>
        <template #actions="{ row }">
          <Button variant="ghost" size="sm" @click="openCallLog(row)">
            {{ t('visualGameAdmin.llm.recon.table.view') }}
          </Button>
        </template>
      </Table>

      <footer class="recon__pagination">
        <Pagination
          :has-next="Boolean(nextCursor)"
          :has-prev="cursorStack.length > 0"
          :loading="loading"
          :prev-label="t('visualGameAdmin.llm.recon.pagination.prev')"
          :next-label="t('visualGameAdmin.llm.recon.pagination.next')"
          @next="nextPage"
          @prev="previousPage"
        />
      </footer>
    </Section>
  </div>
</template>

<style scoped>
.recon__state {
  display: grid;
  justify-items: start;
  gap: var(--drama-space-md);
  padding: var(--drama-space-xl);
}

.recon__pagination {
  display: flex;
  justify-content: flex-end;
  padding: var(--drama-space-lg) var(--drama-space-xl);
  border-top: 1px solid var(--drama-color-border);
}
</style>
